import { SignalTies } from '@cadstrom/cadstrom-shared';
import { Utils } from '@cadstrom/sigma-engine-shared';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import Chip from '@mui/material/Chip';

import type { ReactNode } from 'react';

export interface SignalTieChipProps {
	tie: SignalTies.FlatSignalTie;
	/**
	 * The tie's target, rendered as given: a plain name, or a signal chip when the caller holds the
	 * signal. Callers rendering a bare name should wrap it in `ModelText` so overbars survive.
	 */
	target: ReactNode;
}

/**
 * A chip component for displaying signal tie information.
 * Shows the target signal and optionally the external component details (R, C, L with value and variance).
 */
export function SignalTieChip({ tie, target }: SignalTieChipProps) {
	return (
		<Chip
			color='gray'
			// The target is the only part of a tie whose width has no bound, so it is what gives way.
			classes={{ label: 'min-w-0' }}
			label={
				<div className='flex min-w-0 items-center whitespace-pre'>
					{target}
					{tie.componentType !== null ?
						<>
							<ArrowLeftIcon className='shrink-0 text-lg text-current' />
							{(() => {
								let nodes: ReactNode[] = [];
								switch (tie.componentType) {
									case SignalTies.SignalTieComponentType.Capacitor: {
										nodes.push(<span className='font-bold'>C</span>);
										break;
									}
									case SignalTies.SignalTieComponentType.CapacitorPolarized: {
										// The target sits left of the symbol and the carrying signal right of it, so the
										// plus goes on whichever side the positive pin faces.
										nodes.push(
											<span className='font-bold'>
												{tie.polarity === SignalTies.SignalTiePolarity.PositiveOnTarget ?
													'+C'
												: tie.polarity === SignalTies.SignalTiePolarity.PositiveOnSignal ?
													'C+'
												:	'C?'}
											</span>,
										);
										break;
									}
									case SignalTies.SignalTieComponentType.Inductor: {
										nodes.push(<span className='font-bold'>L</span>);
										break;
									}
									case SignalTies.SignalTieComponentType.Resistor: {
										nodes.push(<span className='font-bold'>R</span>);
										break;
									}
								}

								if (tie.componentValue?.type === 'absolute') {
									const formattedValue = Utils.formatNumericalSIValue(tie.componentValue.value);
									const unit = SignalTies.TIE_COMPONENT_TYPE_TO_UNITS[tie.componentType].short;
									nodes.push(
										<span className='ml-1'>
											{' '}
											{formattedValue} {unit}
										</span>,
									);

									if (tie.componentValue.variance !== null) {
										nodes.push(
											<span> &plusmn; </span>,
											tie.componentValue.varianceType === SignalTies.SignalTieVarianceType.Relative ?
												roundToNearest(tie.componentValue.variance, 0.01) + '%'
											:	Utils.formatNumericalSIValue(tie.componentValue.variance),
										);
									}
								}

								if (tie.componentValue?.type === 'range') {
									if (tie.componentValue.min !== null && tie.componentValue.max !== null) {
										const formattedMin = Utils.formatNumericalSIValue(tie.componentValue.min);
										const formattedMax = Utils.formatNumericalSIValue(tie.componentValue.max);
										const unit = SignalTies.TIE_COMPONENT_TYPE_TO_UNITS[tie.componentType].short;
										nodes.push(
											<span className='ml-1'>
												{' '}
												{formattedMin} to {formattedMax} {unit}
											</span>,
										);
									} else if (tie.componentValue.min !== null) {
										const formattedMin = Utils.formatNumericalSIValue(tie.componentValue.min);
										const unit = SignalTies.TIE_COMPONENT_TYPE_TO_UNITS[tie.componentType].short;
										nodes.push(
											<span className='ml-1'>
												{' '}
												≥ {formattedMin} {unit}
											</span>,
										);
									} else if (tie.componentValue.max !== null) {
										const formattedMax = Utils.formatNumericalSIValue(tie.componentValue.max);
										const unit = SignalTies.TIE_COMPONENT_TYPE_TO_UNITS[tie.componentType].short;
										nodes.push(
											<span className='ml-1'>
												{' '}
												≤ {formattedMax} {unit}
											</span>,
										);
									}
								}

								return nodes;
							})()}
						</>
					:	null}
				</div>
			}
		/>
	);
}

function roundToNearest(value: number, precision: number) {
	return Math.round(value / precision) * precision;
}
